import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { fetchNotifications, fetchUnreadCount, markAsRead, markAllAsRead, deleteOwnNotification, deleteAllOwnNotifications } from '@/services/notifications';
import { playNotificationSound } from '@/lib/audioManager';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import type { Notification } from '@/types/notifications';

const getDynamicLogoUrl = (): string => {
  try {
    const cached = localStorage.getItem('forja:logo:v1');
    if (cached) {
      const { url } = JSON.parse(cached);
      if (url) return url;
    }
  } catch {}
  return '/favicon.png';
};

const shownNotificationIds = new Set<string>();
let notifChannel: BroadcastChannel | null = null;
try {
  notifChannel = new BroadcastChannel('forja:notifications');
  notifChannel.onmessage = (event) => {
    if (event.data?.type === 'shown' && event.data?.id) {
      shownNotificationIds.add(event.data.id);
    }
  };
} catch {}

const claimNotification = (id: string): boolean => {
  if (shownNotificationIds.has(id)) return false;
  shownNotificationIds.add(id);
  try { notifChannel?.postMessage({ type: 'shown', id }); } catch {}
  setTimeout(() => shownNotificationIds.delete(id), 30_000);
  return true;
};

const updateAppBadge = (unread: number) => {
  if (!('setAppBadge' in navigator)) return;
  try {
    if (unread > 0) {
      (navigator as any).setAppBadge();
    } else {
      (navigator as any).clearAppBadge();
    }
  } catch {}
};

export function useNotifications() {
  const { user } = useAuth();
  const { subscribe: subscribePush, isSubscribed: isPushSubscribed, isSupported: isPushSupported } = usePushSubscription();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const autoSubscribeAttempted = useRef(false);

  const loadNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      const [notifs, count] = await Promise.all([
        fetchNotifications(),
        fetchUnreadCount()
      ]);
      // Defensive dedup: if the API ever returns the same id twice (e.g. during
      // concurrent realtime windows), keep only the first occurrence.
      const unique = Array.from(new Map(notifs.map(n => [n.id, n])).values());
      setNotifications(unique);
      setUnreadCount(count);
      updateAppBadge(count);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        updateAppBadge(unreadCount);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [unreadCount]);

  // Auto-subscribe: when permission granted but push not confirmed
  useEffect(() => {
    if (
      !user ||
      !isPushSupported ||
      isPushSubscribed ||
      autoSubscribeAttempted.current ||
      !('Notification' in window) ||
      window.Notification.permission !== 'granted'
    ) return;

    const status = localStorage.getItem('sound-permission-status');
    // Retry for any status where permission was granted but push not confirmed
    if (status !== 'retry-needed' && status !== 'accepted' && status !== 'push-timeout') return;

    autoSubscribeAttempted.current = true;
    logger.log('[Notifications] Auto-subscribe starting (status:', status, ')');

    let cancelled = false;
    const maxAttempts = 3;

    const attemptSubscribe = async (attempt: number) => {
      if (cancelled) return;

      logger.log(`[Notifications] Push attempt ${attempt}/${maxAttempts}`);
      let success = false;
      try {
        success = await Promise.race([
          subscribePush(),
          new Promise<false>(resolve => setTimeout(() => resolve(false), 45000)),
        ]);
      } catch {
        success = false;
      }

      if (success) {
        logger.log(`[Notifications] Push registered on attempt ${attempt}`);
        localStorage.setItem('sound-permission-status', 'push-registered');
        return;
      }

      if (cancelled || attempt >= maxAttempts) {
        console.warn(`[Notifications] Gave up after ${attempt} attempts`);
        // Keep retry-needed so next session tries again
        localStorage.setItem('sound-permission-status', 'retry-needed');
        return;
      }

      // Shorter delays: 5s, 15s (instead of 10s, 20s, 40s...)
      const delay = attempt === 1 ? 5000 : 15000;
      logger.log(`[Notifications] Retrying in ${delay / 1000}s...`);
      setTimeout(() => attemptSubscribe(attempt + 1), delay);
    };

    // Start immediately for first session attempt, 3s delay otherwise
    const initialDelay = status === 'push-timeout' ? 5000 : 3000;
    const startTimer = setTimeout(() => attemptSubscribe(1), initialDelay);

    return () => {
      cancelled = true;
      clearTimeout(startTimer);
    };
  }, [user, isPushSupported, isPushSubscribed, subscribePush]);

  // Real-time subscription
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as Notification;
          let wasAdded = false;
          setNotifications(prev => {
            if (prev.some(n => n.id === newNotification.id)) {
              return prev;
            }
            wasAdded = true;
            return [newNotification, ...prev];
          });

          if (!wasAdded) {
            // Already in state (race between initial load and realtime, or
            // duplicate event delivery on reconnect). Skip side-effects.
            return;
          }

          if (!newNotification.is_read) {
            setUnreadCount(prev => {
              const next = prev + 1;
              updateAppBadge(next);
              return next;
            });
          }

          playNotificationSound();

          if (
            !isPushSubscribed &&
            'Notification' in window &&
            window.Notification.permission === 'granted' &&
            claimNotification(newNotification.id)
          ) {
            new window.Notification(newNotification.title || 'ForjaHub', {
              body: newNotification.message,
              icon: getDynamicLogoUrl(),
              tag: newNotification.id
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const updated = payload.new as Notification;
          setNotifications(prev => 
            prev.map(n => n.id === updated.id ? updated : n)
          );
          loadNotifications();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const deletedId = (payload.old as { id: string }).id;
          setNotifications(prev => prev.filter(n => n.id !== deletedId));
          setUnreadCount(prev => {
            const next = Math.max(0, prev - 1);
            updateAppBadge(next);
            return next;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, loadNotifications]);

  const handleMarkAsRead = useCallback(async (notificationId: string) => {
    try {
      await markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => {
        const next = Math.max(0, prev - 1);
        updateAppBadge(next);
        return next;
      });
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  }, []);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      updateAppBadge(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, []);

  const handleDeleteNotification = useCallback(async (notificationId: string) => {
    try {
      const wasUnread = notifications.find(n => n.id === notificationId && !n.is_read);
      await deleteOwnNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (wasUnread) {
        setUnreadCount(prev => {
          const next = Math.max(0, prev - 1);
          updateAppBadge(next);
          return next;
        });
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, [notifications]);

  const handleDeleteAllNotifications = useCallback(async () => {
    try {
      await deleteAllOwnNotifications();
      setNotifications([]);
      setUnreadCount(0);
      updateAppBadge(0);
    } catch (error) {
      console.error('Error deleting all notifications:', error);
    }
  }, []);

  // Listen for notification clicks coming from the Service Worker (push click).
  // The SW already calls the DB RPC to mark as read; here we sync local state
  // immediately so the bell and list update without waiting for realtime.
  useEffect(() => {
    if (!user || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== 'forja:notification-clicked' || !data.notificationId) return;
      const id = data.notificationId as string;
      let wasUnread = false;
      setNotifications(prev => prev.map(n => {
        if (n.id === id) {
          if (!n.is_read) wasUnread = true;
          return { ...n, is_read: true };
        }
        return n;
      }));
      if (wasUnread) {
        setUnreadCount(prev => {
          const next = Math.max(0, prev - 1);
          updateAppBadge(next);
          return next;
        });
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [user]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    deleteNotification: handleDeleteNotification,
    deleteAllNotifications: handleDeleteAllNotifications,
    refresh: loadNotifications,
  };
}
